import { Star } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, actions, testId }) {
  return (
    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
      <div>
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="font-display font-bold text-3xl tracking-tight" data-testid={testId}>{title}</h1>
        {subtitle && <p className="text-sm text-neutral-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Panel({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-2xl border border-neutral-200/70 ${className}`} {...rest}>{children}</div>;
}

const TONES = {
  neutral: "bg-neutral-100 text-neutral-700",
  yellow: "bg-yellow-100 text-yellow-800",
  blue: "bg-blue-100 text-blue-700",
  indigo: "bg-indigo-100 text-indigo-700",
  green: "bg-emerald-100 text-emerald-700",
  red: "bg-red-100 text-red-700",
  orange: "bg-orange-100 text-orange-700",
};

export const STATUS_TONE = {
  new: "yellow", reviewed: "blue", approved: "indigo", ordered: "indigo", delivered: "green", completed: "neutral",
  open: "yellow", done: "green", not_started: "neutral", in_progress: "blue",
  low: "neutral", normal: "blue", high: "orange", urgent: "red",
};

export function Badge({ children, tone, className = "", ...rest }) {
  const t = TONES[tone || STATUS_TONE[String(children).toLowerCase()] || "neutral"];
  return (
    <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize whitespace-nowrap ${t} ${className}`} {...rest}>
      {String(children).replace(/_/g, " ")}
    </span>
  );
}

export function Stars({ value = 0, size = "w-4 h-4", onChange }) {
  return (
    <span className="inline-flex gap-0.5" data-testid="stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)}
          className={onChange ? "hover:scale-110 transition-transform" : "cursor-default"} data-testid={`star-${n}`}>
          <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`}/>
        </button>
      ))}
    </span>
  );
}

export function Select({ className = "", children, ...rest }) {
  return (
    <select className={`h-10 rounded-lg border border-neutral-200 bg-white text-sm px-3 ${className}`} {...rest}>{children}</select>
  );
}

export function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}
export function fmtD(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString([], { dateStyle: "medium" }); } catch { return iso; }
}

export function ActionTile({ icon: Icon, label, hint, onClick, to, tone = "bg-neutral-900 text-white", testId }) {
  const Comp = "button";
  return (
    <Comp onClick={onClick} data-testid={testId}
      className={`group text-left rounded-2xl p-5 ${tone} transition-transform hover:-translate-y-0.5 active:translate-y-0 w-full`}>
      <Icon className="w-7 h-7 mb-4 opacity-90"/>
      <div className="font-display font-bold text-lg leading-tight">{label}</div>
      {hint && <div className="text-xs opacity-70 mt-1">{hint}</div>}
    </Comp>
  );
}
